import round from 'lodash/round';

import { IconButton, Icons, Tooltip } from '@cast/design-system';
import { downloadFile } from '@cast/utils';

import {
  isCostOptimizedWorkload,
  isPerformanceOptimizedWorkload,
} from 'features/cluster/workload-optimization/impact/_utils';
import { PreparedWorkload } from 'features/workload-optimization';
import { PreparedContainer } from 'features/workload-optimization/types';

import { useSearchContext } from '@cast/shared-search-ui';

const escapeCSV = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const fmt = (value: number | null | undefined): string => {
  if (value == null) return '';
  return String(round(value, 3));
};

const fmtSavings = (value: number | undefined): string => {
  if (value === undefined) return '';
  return round(value, 2).toFixed(2);
};

const getOptimizedLabel = (workload: PreparedWorkload): string => {
  if (isCostOptimizedWorkload(workload)) return 'COST';
  if (isPerformanceOptimizedWorkload(workload)) return 'PERFORMANCE';
  return '';
};

const HEADERS = [
  'workload_name',
  'namespace',
  'kind',
  'container_name',
  'runtime',
  'replicas',
  'pod_count',
  'scaling_policy',
  'managed_by',
  'vpa_management',
  'vpa_apply_type',
  'hpa_management',
  'hpa_min_replicas',
  'hpa_max_replicas',
  'current_cpu_request_cores',
  'current_mem_request_gib',
  'current_cpu_limit_cores',
  'current_mem_limit_gib',
  'original_cpu_request_cores',
  'original_mem_request_gib',
  'original_cpu_limit_cores',
  'original_mem_limit_gib',
  'rec_cpu_request_cores',
  'rec_mem_request_gib',
  'rec_cpu_limit_cores',
  'rec_mem_limit_gib',
  'cost_requested_per_hour',
  'cost_recommended_per_hour',
  'cost_original_per_hour',
  'savings_per_month',
  'optimized',
];

const toContainerRow = (
  w: PreparedWorkload,
  container: PreparedContainer
): string[] => [
  w.name,
  w.namespace,
  w.kind,
  container.name,
  container.runtime ?? '',
  String(w.replicas),
  String(w.podCount),
  w.scalingPolicyName,
  w.managedBy,
  w.workloadConfigV2.vpaConfig.managementOption,
  w.workloadConfigV2.vpaConfig.applyType,
  w.workloadConfigV2.hpaConfig.managementOption,
  fmt(w.workloadConfigV2.hpaConfig.minReplicas),
  fmt(w.workloadConfigV2.hpaConfig.maxReplicas),
  fmt(container.resources?.requests?.cpuCores),
  fmt(container.resources?.requests?.memoryGib),
  fmt(container.resources?.limits?.cpuCores),
  fmt(container.resources?.limits?.memoryGib),
  fmt(container.originalResources?.requests?.cpuCores),
  fmt(container.originalResources?.requests?.memoryGib),
  fmt(container.originalResources?.limits?.cpuCores),
  fmt(container.originalResources?.limits?.memoryGib),
  fmt(container.recommendation?.requests?.cpuCores),
  fmt(container.recommendation?.requests?.memoryGib),
  fmt(container.recommendation?.limits?.cpuCores),
  fmt(container.recommendation?.limits?.memoryGib),
  fmt(w.costsPerHour?.requested),
  fmt(w.costsPerHour?.recommended),
  fmt(w.costsPerHour?.originalRequested),
  fmtSavings(w.currentMonthlySavings),
  getOptimizedLabel(w),
];

const buildCSV = (workloads: PreparedWorkload[]): string => {
  const rows: string[][] = [HEADERS];
  for (const w of workloads) {
    for (const container of w.containers) {
      rows.push(toContainerRow(w, container));
    }
  }
  return rows.map((row) => row.map(escapeCSV).join(',')).join('\n');
};

export const ExportCSVButton = () => {
  const { filteredData } = useSearchContext<PreparedWorkload>() ?? {};
  const hasData = !!filteredData?.length;

  const handleExport = () => {
    if (!filteredData?.length) return;
    const csv = buildCSV(filteredData);
    downloadFile(csv, 'woop-impact.csv');
  };

  return (
    <Tooltip title="Export to CSV" placement="top">
      <span>
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleExport}
          disabled={!hasData}
          aria-label="Export to CSV"
          testId="export-csv-button"
        >
          <Icons.DownloadSimple size={16} />
        </IconButton>
      </span>
    </Tooltip>
  );
};
